// React and Redux Dependencies
import React, { memo, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Components
import SearchField from 'common/TextInput/TextInput';
import SingleCalendar from '../../../common/Input/SingleCalendar';

// Styles
import { FilterWrapper } from 'common/FilterStyles';
import { FlexDiv } from 'commonStyles';
import { CrossIconWrapper, FiletrResetIcon } from '../../styles';

// Redux Actions
import {
    setFilterQuery,
    getBudgetList,
    setApiLoading,
    clearFilterUpdate
} from 'reduxStore/actions/budgetManagement';

// Utils
import { debounce } from 'UtilityComponents/Util';
import { BUDGET_STORE_CONSTANTS } from 'reduxStore/actionConstants';
import { ASSET_PATH } from 'assetsPath';

// Calendar Style
export const calenderStyle = {
    bgColor: '#fff',
    color: '#4a4a4a',
    f_weight: '400',
    top_M_F: '0',
    f_size: '14px',
};

const imgPath = `${ASSET_PATH.IMAGES}budget/filterResetIcon.png`;

const BudgetFilters = () => {
    const dispatch = useDispatch();
    const filters = useSelector(state => state?.budgetManagement?.filters || {});

    /**
     * Local UI-only state
     */
    const [localValues, setLocalValues] = useState({
        BUDGET_NAME: '',
        BUDGET_OWNER: '',
        START_DATE: '',
        END_DATE: '',
    });

    /**
     * Debounced API function — created only once using useCallback.
     */
    const debouncedApiCall = useCallback(
        debounce((updatedFilters) => {
            // Build query
            const query = Object.values(updatedFilters).reduce((acc, f) => {
                if (f?.value) acc[f.filterKey] = f.value;
                return acc;
            }, {});

            const payload = { query, limit: 100, offset: 0 };

            // API call
            dispatch(getBudgetList(payload));
        }, 500),
        [dispatch]
    );

    /**
     * Handles input changes:
     * - UI updates immediately (not debounced)
     * - Redux filters update immediately
     * - API call is debounced
     */
    const handleInputChange = useCallback((value, key) => {
        // Update UI value instantly
        setLocalValues(prev => ({ ...prev, [key]: value }));

        // Build updated filters
        const updatedFilters = {
            ...filters,
            [key]: { ...filters[key], value }
        };

        // Immediate Redux update
        dispatch(setFilterQuery({ updatedFilter: updatedFilters }));

        // Set loading
        dispatch(setApiLoading({
            apiType: BUDGET_STORE_CONSTANTS.LIST_API_LOADING,
            loading: true
        }));

        // Debounced API search
        debouncedApiCall(updatedFilters);
    }, [filters, dispatch, debouncedApiCall]);

    /**  Filter Reset Handler */
    const handleResetClick = useCallback(() => {
        setLocalValues({
            BUDGET_NAME: '',
            BUDGET_OWNER: '',
            START_DATE: '',
            END_DATE: '',
        });
    
        // Clear filter update
        dispatch(clearFilterUpdate());
    
        // Set loading
        dispatch(
            setApiLoading({
                apiType: BUDGET_STORE_CONSTANTS.LIST_API_LOADING,
                loading: true,
            })
        );
    
        // API call
        dispatch(getBudgetList({ limit: 100, offset: 0, refresh: true }));
    }, [dispatch]);
    
    /**
     * Memoized rendering of filters
     */
    const filterComponents = useMemo(() => {
        return Object.keys(filters).map((key) => {
            const filter = filters[key];
            if (!filter) return null;

            const { label } = filter;
            const value = localValues?.[key];

            if (key === 'BUDGET_NAME' || key === 'BUDGET_OWNER') {
                return (
                    <SearchField
                        key={key}
                        $width='194px'
                        $height='32px'
                        btnClass='budgetSearchbtnClass'
                        customClass='budgetSearch'
                        placeHolder={label}
                        type='text'
                        textType='search'
                        value={value}
                        onChange={(e) => handleInputChange(e.target.value, key)}
                        filter={filter}
                    />
                );
            }

            if (key === 'START_DATE' || key === 'END_DATE') {
                return (
                    <SingleCalendar
                        key={key}
                        {...calenderStyle}
                        value={value || null}
                        onChange={(date) => handleInputChange(date, key)}
                        label={!value ? label : null}
                        lblColor='#777'
                        lblSize='14px'
                        leftLblPos="35px"
                        textTransform="none"
                        lblFWeight="400"
                        top_M_F="-6px"
                        inputProps={{
                            placeholder: `Select ${label}`,
                            customClass: 'budgetCalendarClass'
                        }}
                    />
                );
            }

            return null;
        });
    }, [filters, localValues, handleInputChange]);

    return (
        <FilterWrapper>
            <FlexDiv justify='flex-start' align='center'>
                <FlexDiv bottom_P='24' justify='flex-start' $width='80%'>
                    {filterComponents}
                </FlexDiv>
                <CrossIconWrapper onClick={handleResetClick}>
                    <FiletrResetIcon src={imgPath}/>
                </CrossIconWrapper>
            </FlexDiv>
        </FilterWrapper>
    );
};

export default memo(BudgetFilters);
